import { Box, Grid, IconButton, MenuItem, Paper, Select, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Priority } from '../../components/Priority';
import { SubmitControl } from '../../components/SubmitControl';
import useSnackbar from '../../hooks/useSnackbar';
import { IStory, IUserSegment, RelatedStoryType } from '../../services/types';
import { getCoverUrl } from '../../services/utils';
import ObjectSelector from '../home/userSegment/ObjectSelector';
import useStyles from './styles';

interface IProps {
  story: IStory;
  segment?: IUserSegment;
  onCancel: () => void;
  onAdd: (storyId: number, position: RelatedStoryType, segmentId: number, priority: number) => void;
}

const AddRelatedStory = (props: IProps): JSX.Element => {
  const classes = useStyles();

  // states
  const [segment, setSegment] = useState<IUserSegment | undefined>(props.segment);
  const [position, setPosition] = useState<RelatedStoryType>();
  const [priority, setPriority] = useState<number>(0);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);

  // memos
  const coverUrl = useMemo(() => (props.story ? getCoverUrl(props.story, 'h') : null), [props.story]);

  // handlers
  const onAddObject = (object: IUserSegment) => {
    setSegment(object);
  };
  const handleTypeChange = (v: RelatedStoryType) => {
    setPosition(v);
  };
  const onClickAdd = () => {
    props.onAdd(props.story.id, position!, segment!.id!, priority);
  };

  // watches
  useEffect(() => {
    const validations: string[] = [];
    if (segment === undefined) {
      validations.push('Segment not selected');
    }
    if (!position) {
      validations.push('Position not selected');
    }
    if (priority === 0) {
      validations.push('Priority must be greater than 0');
    }

    setValidationMessages(validations);
  }, [segment, position, priority]);

  return (
    <Paper className={classes.addRelatedStoryPopup}>
      <Box padding={2}>
        <Typography className={classes.topTitle}>Add Related Story</Typography>
        <Box className={classes.storyInfoBox} marginTop={2}>
          {coverUrl && (
            <div
              style={{
                backgroundImage: `url(${coverUrl}?w=320)`,
              }}
              className={classes.coverImage}
            />
          )}
          <div className={classes.storyDetailBox}>
            <div>
              <span className={classes.storyLabel}>ID</span>
              <span className={classes.segmentName}>{props.story.id}</span>
            </div>
            <div>
              <span className={classes.storyLabel}>Title</span>
              <span className={classes.segmentName}>{props.story.title}</span>
            </div>
            <div>
              <span className={classes.storyLabel}>Pen Name</span>
              <span className={classes.segmentName}>{props.story.penname.name}</span>
            </div>
          </div>
        </Box>
        <Box marginTop={2} className={classes.mapperDetailForm}>
          <Grid container spacing={1}>
            <Grid item xs={2} className={classes.formLabel}>
              Position
            </Grid>
            <Grid item xs={10}>
              <Select value={position || ''} onChange={e => handleTypeChange(e.target.value as RelatedStoryType)}>
                <MenuItem value={RelatedStoryType.Episode}>Episode end</MenuItem>
                <MenuItem value={RelatedStoryType.StoryCover}>Story Cover</MenuItem>
                <MenuItem value={RelatedStoryType.Paywall}>Paywall</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={2} className={classes.formLabel}>
              Segment
            </Grid>
            <Grid item xs={10}>
              {segment && (
                <div>
                  {segment ? segment.name : ''}
                  <IconButton
                    size="medium"
                    onClick={() => {
                      setSegment(undefined);
                    }}
                  >
                    <DeleteIcon color={'primary'} />
                  </IconButton>
                </div>
              )}
            </Grid>
            {!segment && (
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={2}></Grid>
                  <Grid item xs={10}>
                    <ObjectSelector
                      className={classes.segmentSelectorWrap}
                      type="segment"
                      selectedIds={[]}
                      selectLimit={1}
                      onAddSegment={item => {
                        onAddObject(item as IUserSegment);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={2} className={classes.formLabel}>
              Priority
            </Grid>
            <Grid item xs={10}>
              <Priority
                priority={priority}
                onEnterValue={(v: number) => {
                  setPriority(v);
                }}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <SubmitControl mode={'create'} onCancelClick={props.onCancel} onSubmitClick={onClickAdd} validationMessages={validationMessages} />
        </Box>
      </Box>
    </Paper>
  );
};
export default AddRelatedStory;
